"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Loader, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
function QuoteForm() {
  const [formDef, setFormDef] = React.useState<any>(null);
  const [isLoadingDef, setIsLoadingDef] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string>("Request received. A pro will contact you shortly.");
  const [formData, setFormData] = React.useState({
    full_name: "",
    phone: "",
    email: "",
    service_address: "",
    service_type: "Plumbing",
    message: "",
    consent: false
  });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [hpValue, setHpValue] = React.useState("");
  const fetchTimeRef = React.useRef<number>(Date.now());
  const prefersReducedMotion = useReducedMotion();
  React.useEffect(() => {
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setIsLoadingDef(false);
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request").then((def: any) => {
      setFormDef(def);
      fetchTimeRef.current = Date.now();
      setIsLoadingDef(false);
    }).catch(async (err: any) => {
      if (err?.status === 404) {
        try {
          const list = await client.pluginRequest("wvc-forms/v1/forms");
          const items = list?.items || [];
          const match = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : items.find((f: any) => f.name?.toLowerCase().includes("service") || f.name?.toLowerCase().includes("homeowner") || f.name?.toLowerCase().includes("request")));
          if (match?.key) {
            const recovered = await client.pluginRequest(`wvc-forms/v1/forms/${match.key}`);
            setFormDef(recovered);
            fetchTimeRef.current = Date.now();
          }
        } catch {
          // fallback gracefully
        }
      }
      setIsLoadingDef(false);
    });
  }, []);
  const serviceTypeField = React.useMemo(() => {
    return formDef?.fields?.find((f: any) => f.key === "service_type");
  }, [formDef]);
  const serviceOptions = React.useMemo(() => {
    if (serviceTypeField?.options && serviceTypeField.options.length > 0) {
      return serviceTypeField.options;
    }
    return [{
      value: "Plumbing",
      label: "Plumbing"
    }, {
      value: "Electrical",
      label: "Electrical"
    }, {
      value: "HVAC",
      label: "HVAC"
    }, {
      value: "Appliance",
      label: "Appliance"
    }];
  }, [serviceTypeField]);
  React.useEffect(() => {
    if (serviceOptions.length > 0) {
      const match = serviceOptions.find((o: any) => o.value?.toLowerCase() === "plumbing") || serviceOptions[0];
      if (match && !formData.service_type) {
        setFormData(prev => ({
          ...prev,
          service_type: match.value
        }));
      }
    }
  }, [serviceOptions, formData.service_type]);
  const container = {
    hidden: {
      opacity: 1
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08,
        delayChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const item = {
    hidden: prefersReducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.52,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const resetForm = () => {
    setFormData({
      full_name: "",
      phone: "",
      email: "",
      service_address: "",
      service_type: serviceOptions[0]?.value || "Plumbing",
      message: "",
      consent: false
    });
    setFieldErrors({});
    setHpValue("");
    setSubmitError(null);
    setIsSubmitted(false);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errors: Record<string, string> = {};
    if (!formData.full_name.trim() || formData.full_name.trim().length < 2) {
      errors.full_name = "Please enter your full name.";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Please enter your phone number.";
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!formData.service_address.trim()) {
      errors.service_address = "Please enter the service address.";
    }
    if (!formData.service_type) {
      errors.service_type = "Please select a service type.";
    }
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      errors.message = "Describe the issue (min 10 chars)";
    }
    if (!formData.consent) {
      errors.consent = "Confirm to proceed";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest || !formDef) {
        throw new Error("Forms service unavailable");
      }
      const minSeconds = formDef.spam?.min_seconds ?? 3;
      const elapsed = (Date.now() - fetchTimeRef.current) / 1000;
      if (elapsed < minSeconds) {
        await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsed) * 1000)));
      }
      const fieldMap: Record<string, string> = {};
      if (formDef.fields) {
        for (const f of formDef.fields) {
          fieldMap[f.key] = f.id;
        }
      }
      const values: Record<string, any> = {};
      if (fieldMap.full_name) values[fieldMap.full_name] = formData.full_name;
      if (fieldMap.email) values[fieldMap.email] = formData.email;
      if (fieldMap.phone) values[fieldMap.phone] = formData.phone;
      if (fieldMap.service_type) values[fieldMap.service_type] = formData.service_type;
      if (fieldMap.service_address) values[fieldMap.service_address] = formData.service_address;
      if (fieldMap.message) values[fieldMap.message] = formData.message;
      if (fieldMap.appliance_type) values[fieldMap.appliance_type] = "";
      if (fieldMap.source_page) values[fieldMap.source_page] = "Plumbing Services";
      if (fieldMap.source_city) values[fieldMap.source_city] = "";
      if (fieldMap.source_service) values[fieldMap.source_service] = "Plumbing Services";
      if (fieldMap.consent) values[fieldMap.consent] = formData.consent ? "1" : "";
      if (formDef.fields) {
        for (const f of formDef.fields) {
          if (f.type === "hidden" && f.default && values[f.id] === undefined) {
            values[f.id] = f.default;
          }
        }
      }
      const payload: any = {
        values,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const targetKey = formDef.key || "homeowner_service_request";
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${targetKey}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        if (res.confirmation?.message) {
          setSuccessMessage(res.confirmation.message.replace(/<[^>]*>?/gm, "") || "Request received. A pro will contact you shortly.");
        }
        setIsSubmitted(true);
      } else if (res?.code === "frms_validation_failed") {
        const errorsByKey: Record<string, string> = {};
        if (res.errors && formDef.fields) {
          for (const field of formDef.fields) {
            if (res.errors[field.id]) {
              errorsByKey[field.key] = res.errors[field.id].message;
            }
          }
        }
        setFieldErrors(errorsByKey);
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else {
        setSubmitError(res?.message || "Submission failed. Please try again.");
      }
    } catch {
      setSubmitError("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 md:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
          Request sent
        </p>
        <p className="mt-4 font-mono text-2xl uppercase leading-tight text-foreground md:text-3xl">
          {successMessage}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="mt-8 rounded-lg border-border bg-transparent px-7 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-foreground hover:bg-primary hover:text-primary-foreground">
          New request
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-8">
      {formDef?.spam?.honeypot?.field && <div style={{
      position: "absolute",
      left: "-9999px"
    }} aria-hidden="true">
          <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
        </div>}

      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      amount: 0.2
    }} className="space-y-7">
        <motion.div variants={item} className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
          <div className="grid gap-3">
            <label className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Full Name
            </label>
            <Input placeholder="Jane Smith" value={formData.full_name} onChange={e => {
            setFormData(prev => ({
              ...prev,
              full_name: e.target.value
            }));
            if (fieldErrors.full_name) {
              setFieldErrors(prev => ({
                ...prev,
                full_name: ""
              }));
            }
          }} className="h-11 border-0 border-b-[1.5px] border-border bg-transparent px-3 text-base text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
            {fieldErrors.full_name && <p className="text-sm text-destructive">{fieldErrors.full_name}</p>}
          </div>

          <div className="grid gap-3">
            <label className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Phone
            </label>
            <Input type="tel" placeholder="(555) 000-0000" value={formData.phone} onChange={e => {
            setFormData(prev => ({
              ...prev,
              phone: e.target.value
            }));
            if (fieldErrors.phone) {
              setFieldErrors(prev => ({
                ...prev,
                phone: ""
              }));
            }
          }} className="h-11 border-0 border-b-[1.5px] border-border bg-transparent px-3 text-base text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
            {fieldErrors.phone && <p className="text-sm text-destructive">{fieldErrors.phone}</p>}
          </div>
        </motion.div>

        <motion.div variants={item}>
          <div className="grid gap-3">
            <label className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Email
            </label>
            <Input type="email" placeholder="jane@email.com" value={formData.email} onChange={e => {
            setFormData(prev => ({
              ...prev,
              email: e.target.value
            }));
            if (fieldErrors.email) {
              setFieldErrors(prev => ({
                ...prev,
                email: ""
              }));
            }
          }} className="h-11 border-0 border-b-[1.5px] border-border bg-transparent px-3 text-base text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
            {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
          </div>
        </motion.div>

        <motion.div variants={item}>
          <div className="grid gap-3">
            <label className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Service Address
            </label>
            <Input placeholder="Street address, city, state, ZIP" value={formData.service_address} onChange={e => {
            setFormData(prev => ({
              ...prev,
              service_address: e.target.value
            }));
            if (fieldErrors.service_address) {
              setFieldErrors(prev => ({
                ...prev,
                service_address: ""
              }));
            }
          }} className="h-11 border-0 border-b-[1.5px] border-border bg-transparent px-3 text-base text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
            {fieldErrors.service_address && <p className="text-sm text-destructive">{fieldErrors.service_address}</p>}
          </div>
        </motion.div>

        <motion.div variants={item}>
          <div className="grid gap-3">
            <label className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Service Type
            </label>
            <Select value={formData.service_type} onValueChange={val => {
            setFormData(prev => ({
              ...prev,
              service_type: val
            }));
            if (fieldErrors.service_type) {
              setFieldErrors(prev => ({
                ...prev,
                service_type: ""
              }));
            }
          }}>
              <SelectTrigger className="h-11 w-full border-0 border-b-[1.5px] border-border bg-transparent px-3 text-base text-foreground transition-colors focus-visible:border-primary focus-visible:ring-0 data-[placeholder]:text-muted-foreground">
                <SelectValue placeholder="Choose service" />
              </SelectTrigger>
              <SelectContent className="rounded-md border-border bg-popover text-popover-foreground">
                {serviceOptions.map((opt: any) => <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            {fieldErrors.service_type && <p className="text-sm text-destructive">{fieldErrors.service_type}</p>}
          </div>
        </motion.div>

        <motion.div variants={item}>
          <div className="grid gap-3">
            <label className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Describe the Problem
            </label>
            <Textarea rows={5} placeholder="Tell us what your AC, HVAC system, or appliance is doing..." value={formData.message} onChange={e => {
            setFormData(prev => ({
              ...prev,
              message: e.target.value
            }));
            if (fieldErrors.message) {
              setFieldErrors(prev => ({
                ...prev,
                message: ""
              }));
            }
          }} className="min-h-32 border-0 border-b-[1.5px] border-border bg-transparent px-3 py-3 text-base text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0" />
            {fieldErrors.message && <p className="text-sm text-destructive">{fieldErrors.message}</p>}
          </div>
        </motion.div>

        <motion.div variants={item}>
          <div className="flex flex-row items-start gap-3">
            <Checkbox id="booking-consent" checked={formData.consent} onCheckedChange={checked => {
            setFormData(prev => ({
              ...prev,
              consent: checked === true
            }));
            if (fieldErrors.consent) {
              setFieldErrors(prev => ({
                ...prev,
                consent: ""
              }));
            }
          }} className="mt-1 border-border data-[state=checked]:border-primary data-[state=checked]:bg-primary" />
            <div className="grid gap-1.5 leading-none">
              <label htmlFor="booking-consent" className="cursor-pointer text-sm font-normal normal-case tracking-normal text-muted-foreground">
                I agree to be contacted about this request.
              </label>
              {fieldErrors.consent && <p className="text-sm text-destructive">{fieldErrors.consent}</p>}
            </div>
          </div>
        </motion.div>

        {submitError && <motion.div variants={item} className="border-l-2 border-destructive bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive">{submitError}</p>
          </motion.div>}

        <motion.div variants={item} className="pt-2 md:flex md:justify-end">
          <Button type="submit" disabled={isSubmitting || isLoadingDef} className="h-auto w-full rounded-lg bg-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.08em] text-primary-foreground hover:bg-primary/90 md:w-auto">
            {isSubmitting ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                Sending
              </> : "Submit Request"}
          </Button>
        </motion.div>
      </motion.div>
    </form>;
}
export default function Booking() {
  const prefersReducedMotion = useReducedMotion();
  const leftContainer = {
    hidden: {
      opacity: 1
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const leftItem = {
    hidden: prefersReducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.52,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section data-section-id="24734" id="bookingrequest" className="bg-background text-foreground">
      <div className="mx-auto max-w-[1120px] px-5 py-32 sm:px-8 md:py-44 lg:px-14">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-20">
          <motion.div variants={leftContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5">
            <motion.div variants={leftItem} className="flex items-center gap-3">
              <span className="block h-[10px] w-px bg-primary" aria-hidden="true" />
              <span className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-primary">
                Book a Pro
              </span>
              <span className="block h-[10px] w-px bg-primary" aria-hidden="true" />
            </motion.div>

            <motion.h2 variants={leftItem} className="mt-6 font-mono text-[clamp(2.5rem,5.2vw,5.25rem)] font-bold uppercase leading-[0.95] tracking-[-0.005em] text-foreground">
              Get your home fixed today.
            </motion.h2>

            <motion.p variants={leftItem} className="mt-8 max-w-md text-[clamp(1.0625rem,1.15vw,1.1875rem)] leading-[1.55] text-muted-foreground">
              Connect with verified local contractors for fast, reliable home service repairs and maintenance.
            </motion.p>

            <motion.div variants={leftItem} className="mt-12 space-y-4 border-t border-border pt-8">
              <div className="flex items-center gap-3 text-sm tracking-[0.02em] text-muted-foreground">
                <Phone className="size-4 text-primary" strokeWidth={1.5} />
                <span>(470) 847-9076</span>
              </div>

              <div className="flex items-center gap-3 text-sm tracking-[0.02em] text-muted-foreground">
                <Clock className="size-4 text-primary" strokeWidth={1.5} />
                <span>Ask your pro about availability</span>
              </div>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </div>
    </section>;
}